/** Partner console → Team. Reseller administrators invite teammates by email
 *  + role (administrator / manager / billing), change roles, and remove them.
 *  Admin-only; the nav hides it for other roles, and this guards direct URL
 *  access too. */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { PartnerPageHeader } from "@/components/PartnerLayout";
import { Spinner } from "@/components/Spinner";
import {
  type PartnerRole,
  useInvitePartnerMember,
  usePartnerMe,
  usePartnerMembers,
  useRemovePartnerMember,
  useSetPartnerMemberRole,
} from "@/lib/partner";

const ROLES: PartnerRole[] = ["administrator", "manager", "billing"];

function RoleLabel({ role }: { role: PartnerRole }) {
  const { t } = useTranslation();
  return <>{t(`partnerTeam.role.${role}`)}</>;
}

export default function PartnerTeamPage() {
  const { t } = useTranslation();
  const me = usePartnerMe();
  const members = usePartnerMembers(me.data?.partner_role === "administrator");
  const invite = useInvitePartnerMember();
  const setRole = useSetPartnerMemberRole();
  const remove = useRemovePartnerMember();

  const [email, setEmail] = useState("");
  const [role, setRole_] = useState<PartnerRole>("manager");
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  if (me.isLoading) return <Spinner fullscreen label={t("common.loading")} />;
  if (me.data?.partner_role !== "administrator") {
    return (
      <div className="text-sm text-nb-text-2">{t("partnerTeam.adminOnly")}</div>
    );
  }

  const submitInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    setInviteUrl(null);
    try {
      const res = (await invite.mutateAsync({ email: email.trim(), role })) as {
        url?: string;
      };
      setInviteUrl(res.url ?? null);
      setEmail("");
    } catch (e2) {
      const detail = (e2 as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      setErr(detail || t("partnerTeam.inviteError"));
    }
  };

  const data = members.data;

  return (
    <div className="space-y-6">
      <PartnerPageHeader
        title={t("partnerTeam.title")}
        description={t("partnerTeam.description")}
      />

      {/* Invite */}
      <section className="rounded-md border border-nb-border bg-nb-surface/30 p-4">
        <h2 className="mb-3 text-sm font-semibold text-nb-text">
          {t("partnerTeam.inviteHeading")}
        </h2>
        <form className="flex flex-wrap items-end gap-2" onSubmit={submitInvite}>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t("partnerTeam.emailPlaceholder")}
            className="min-w-[240px] flex-1 rounded-md border border-nb-border bg-nb-bg p-2 text-sm"
            data-testid="invite-email"
          />
          <select
            value={role}
            onChange={(e) => setRole_(e.target.value as PartnerRole)}
            className="rounded-md border border-nb-border bg-nb-bg p-2 text-sm"
            data-testid="invite-role"
          >
            {ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`partnerTeam.role.${r}`)}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="btn-primary text-sm"
            disabled={invite.isPending || !email.trim()}
            data-testid="invite-submit"
          >
            {invite.isPending
              ? t("partnerTeam.inviting")
              : t("partnerTeam.sendInvite")}
          </button>
        </form>
        {err && (
          <p className="mt-2 text-xs text-cvp-warn" role="alert">
            {err}
          </p>
        )}
        {inviteUrl && (
          <p className="mt-2 break-all text-xs text-nb-text-2" role="status">
            {t("partnerTeam.inviteSent")}{" "}
            <span className="font-mono text-nb-text-3">{inviteUrl}</span>
          </p>
        )}
      </section>

      {/* Members */}
      <section className="space-y-2">
        <h2 className="text-sm font-semibold text-nb-text">
          {t("partnerTeam.membersHeading")}
        </h2>
        {members.isLoading ? (
          <div className="text-sm text-nb-text-3">{t("common.loading")}</div>
        ) : (
          <ul className="divide-y divide-nb-border rounded-md border border-nb-border">
            {(data?.members ?? []).map((m) => (
              <li
                key={m.id}
                className="flex flex-wrap items-center justify-between gap-3 p-3"
              >
                <div className="min-w-0">
                  <span className="text-sm text-nb-text">{m.email}</span>
                  {(m.first_name || m.last_name) && (
                    <span className="ml-2 text-xs text-nb-text-3">
                      {m.first_name} {m.last_name}
                    </span>
                  )}
                  {m.is_self && (
                    <span className="ml-2 text-xs text-nb-mint">
                      {t("partnerTeam.you")}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {m.is_self ? (
                    <span className="text-xs text-nb-text-2">
                      <RoleLabel role={m.role} />
                    </span>
                  ) : (
                    <select
                      value={m.role}
                      onChange={(e) =>
                        setRole.mutate({
                          userId: m.id,
                          role: e.target.value as PartnerRole,
                        })
                      }
                      className="rounded-md border border-nb-border bg-nb-bg p-1 text-xs"
                      data-testid={`member-role-${m.id}`}
                    >
                      {ROLES.map((r) => (
                        <option key={r} value={r}>
                          {t(`partnerTeam.role.${r}`)}
                        </option>
                      ))}
                    </select>
                  )}
                  {!m.is_self && (
                    <button
                      type="button"
                      className="text-xs text-cvp-warn hover:underline"
                      onClick={() => remove.mutate(m.id)}
                      data-testid={`member-remove-${m.id}`}
                    >
                      {t("partnerTeam.remove")}
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Pending invites */}
      {(data?.pending_invites?.length ?? 0) > 0 && (
        <section className="space-y-2">
          <h2 className="text-sm font-semibold text-nb-text">
            {t("partnerTeam.pendingHeading")}
          </h2>
          <ul className="divide-y divide-nb-border rounded-md border border-nb-border">
            {data!.pending_invites.map((inv) => (
              <li
                key={inv.id}
                className="flex items-center justify-between gap-3 p-3 text-sm"
              >
                <span className="text-nb-text-2">{inv.email}</span>
                <span className="text-xs text-nb-text-3">
                  <RoleLabel role={inv.role} /> · {t("partnerTeam.pending")}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
